/**
 * MapTicket — Map ticket content for EditorPanel.
 * Extracted from EditorPanel.tsx (refactoring, no behavior changes).
 */

import { useProjectStore, useUIStore, useMapStore, useMonitoringStore } from '../../../stores';
import { useAuthStore } from '../../../stores/authStore';
import { useIsReadOnlyViewer } from '../../../hooks/useShareViewer';
import { Button } from '../../common';
import { saveProject } from '../../../services/projectApi';

export default function MapTicket() {
  // F-02E-2B: share viewers see no Change-Map (placeholder for future
  // basemap chooser) or Save-View (writes mapCenter/zoom + auto-saves
  // project to server). Location tracking is pure-client GPS — kept.
  const isReadOnly = useIsReadOnlyViewer();
  const isLocationTracking = useMapStore((s) => s.isLocationTracking);
  const locationStatus = useMapStore((s) => s.locationStatus);
  const startLocationTracking = useMapStore((s) => s.startLocationTracking);
  const stopLocationTracking = useMapStore((s) => s.stopLocationTracking);
  const guidePointMode = useMapStore((s) => s.guidePointMode);
  const setGuidePointMode = useMapStore((s) => s.setGuidePointMode);
  const addNotification = useUIStore((s) => s.addNotification);

  const handleGuidePointToggle = () => {
    const next = !guidePointMode;
    if (next) {
      // One armed map mode at a time — disarm add/batch before arming guide.
      const ui = useUIStore.getState();
      ui.setAddMode(false);
      ui.setBatchStatusMode(false);
    }
    setGuidePointMode(next);
  };

  const handleSaveView = async () => {
    const mapState = useMapStore.getState();
    const center = mapState.map?.getCenter();
    useProjectStore.getState().setData({
      mapCenter: center ? [center.lng, center.lat] : mapState.center,
      mapZoom: mapState.map?.getZoom() ?? mapState.zoom,
      mapBearing: mapState.map?.getBearing() ?? mapState.bearing,
      mapPitch: mapState.map?.getPitch() ?? mapState.pitch,
    } as any);

    // Immediately persist to server if logged in with a project
    const { meta, markSaved, setSaveStatus } = useProjectStore.getState();
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    if (meta.siteId && isAuthenticated) {
      setSaveStatus('saving');
      try {
        const data = useProjectStore.getState().data;
        const mon = useMonitoringStore.getState();
        const fullData = {
          ...data,
          ...(mon.projectConfig ? { monitoringConfig: mon.projectConfig } : {}),
          ...(mon.schema ? { monitoringSchema: mon.schema } : {}),
          ...(mon.uploadedSampleData ? { monitoringSampleData: mon.uploadedSampleData } : {}),
        };
        const result = await saveProject(meta.siteId, fullData as any, meta.version);
        if (!('conflict' in result)) {
          useProjectStore.getState().setMeta({ version: result.version });
        }
        markSaved();
        setSaveStatus('saved');
        setTimeout(() => {
          if (useProjectStore.getState().meta.saveStatus === 'saved') {
            useProjectStore.getState().setSaveStatus('idle');
          }
        }, 3000);
        addNotification({ type: 'success', title: 'Map view saved', message: 'Saved to server' });
      } catch (err) {
        console.error('[SaveView] Server save failed:', err);
        setSaveStatus('error');
        addNotification({ type: 'success', title: 'Map view saved locally', message: 'Server save failed — will retry on next auto-save' });
      }
    } else {
      addNotification({ type: 'success', title: 'Map view saved', message: 'Will be restored when you open this project' });
    }
  };

  return (
    <div className="ep-ticket-content" data-testid="map-ticket">
      {!isReadOnly && (
        <>
          <div className="ep-section-label">Map</div>
          <Button variant="secondary" size="sm" fullWidth onClick={() => addNotification({ type: 'info', title: 'Change map', message: 'Coming soon' })}>
            Change Map
          </Button>
          <Button variant="secondary" size="sm" fullWidth onClick={handleSaveView} data-testid="map-ticket-save-view">
            Save View
          </Button>
          <div className="ep-divider" />
        </>
      )}
      <div className="ep-section-label">Location</div>
      <div className="ep-button-row">
        <Button variant="secondary" size="sm" onClick={startLocationTracking} disabled={isLocationTracking}>
          My Location
        </Button>
        <Button variant="ghost" size="sm" onClick={stopLocationTracking} disabled={!isLocationTracking}>
          Stop
        </Button>
      </div>
      <Button
        variant={guidePointMode ? 'primary' : 'secondary'}
        size="sm"
        fullWidth
        onClick={handleGuidePointToggle}
        data-testid="map-ticket-guide-point"
      >
        {guidePointMode ? 'Tap the map to set target…' : 'Guide to Point'}
      </Button>
      {locationStatus && <p className="ep-status-text">{locationStatus}</p>}
    </div>
  );
}
